import React, { FC, ReactNode, useState } from 'react';
import { Menu, MenuItem, Box } from '@mui/material';
import classNames from 'classnames';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import axiosInstant from '../../api';
import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { SettingsVideoModal } from '../../pages/admin/videos/selectedVideoCardV2/SettingsVideoModal';
import { AddToStoryModalV2 } from '../../pages/admin/videos/selectedVideoCardV2/AddToStoryModalV2';
import AddToVideoShortlistMenu from '../../pages/admin/VideoShortlists/AddToVideoShortlistMenu';
import { VariationReviewModal } from '../../pages/videoProduction/components/videoReview/VariationReviewModal';
import { PreviousVersionsModal } from '../../pages/videoProduction/components/videoReview/PreviousVersionsModal';
import { canViewPreviousVersions } from '../../pages/videoProduction/components/videoReview/canViewPreviousVersions';
// eslint-disable-next-line import/order
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import {
  removeVideoFromEmbed,
  deleteVideo,
  videoDeleteImpact,
  getBrandByVideoId,
  unapproveBrandProjectFile,
  reimportFile,
} from '../../api/endpoints';
import { downloadCommentsPDF, getReviewComments } from '../../utils/commentsPdfGenerator';
import { buildCopyPath } from '../../pages/admin/videos/components/buildCopyPath';
import { isAM, canApproveUnapproveVideos, isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { getVideoStatus, getVideoFilename } from './videoStatus';
import { Button } from '../Button/Button';
import { IconPlayBox } from '../../utils/icons';
import { fetchFileComments, usePublicShare } from '../PublicShare/PublicShareContext';
import { DELETED_TOGGLE_LABEL, softDeleteErrorMessage } from './videoSoftDelete';
import { pdpImpactWarning, useDeleteImpact } from '../../pages/videoProduction/components/projectSoftDelete';

const ic = (inner: string, size = 17, sw = 1.7) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: inner }}
  />
);
const ICONS = {
  kebab: ic('<circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none"/>'),
  download: ic('<path d="M12 3v11m0 0-4-4m4 4 4-4"/><path d="M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1"/>'),
  copy: ic('<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M15 5.5A2.5 2.5 0 0 0 12.5 3H5.5A2.5 2.5 0 0 0 3 5.5v7A2.5 2.5 0 0 0 5.5 15"/>'),
  star: ic('<path d="m12 3.6 2.5 5.3 5.7.5-4.3 3.8 1.3 5.6L12 15.9l-5.2 2.9 1.3-5.6-4.3-3.8 5.7-.5z"/>'),
  story: ic('<path d="m12 3 9 5-9 5-9-5z"/><path d="m3 12 9 5 9-5"/>'),
  edit: ic('<path d="m14.5 5.5 4 4L8 20H4v-4z"/><path d="m12.5 7.5 4 4"/>'),
  review: ic('<path d="M20 12a7 7 0 0 1-7 7H8l-4 3v-5.5A7 7 0 0 1 8 5h5a7 7 0 0 1 7 7z"/>'),
  reimport: ic('<path d="M20 11a8 8 0 1 0-2.3 5.7"/><path d="M20 5v6h-6"/>'),
  unapprove: ic('<circle cx="12" cy="12" r="9"/><path d="M8.5 12h7"/>'),
  trash: ic('<path d="M4 7h16"/><path d="M9 7V4.5h6V7"/><path d="M6 7l1 13h10l1-13"/><path d="M10 11v6M14 11v6"/>'),
  comment: ic('<path d="M20 11.5a6.5 6.5 0 0 1-6.5 6.5H9l-4 3v-5.2A6.5 6.5 0 0 1 8.5 5h5A6.5 6.5 0 0 1 20 11.5z"/><path d="M9 11h6"/>'),
  remove: ic('<circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/>'),
};

export interface VideoMenuContext {
  reviewButton?: 'label' | 'compact' | 'icon';
  /** Replaces the old window.location.pathname check. */
  inShortlistDetail?: boolean;
  embedId?: number;
}

interface Props {
  video: IVideo;
  refetch: () => void;
  /** Pre-resolved brand (the video detail page already fetched it). */
  brand?: any;
  project?: any;
  context?: VideoMenuContext;
  onRemoveFromVideoShortlist?: () => void;
  /** Public-share surfaces: comments-only menu; kebab absent without comments. */
  share?: boolean;
  title: string;
  /** Detail-page hosts: called after a successful delete, because refetch()
   *  there re-requests the record that no longer exists. */
  onDeleted?: () => void;
  /** Detail-page hosts: called when the settings modal closes, whatever tab it
   *  was on — its Product Pages tab changes things the page has to re-read. */
  onSettingsClosed?: () => void;
}

interface Entry {
  key: string;
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  reason?: string;
  danger?: boolean;
}

const disabledReason = (conditions: Array<[boolean, string]>): string | undefined => {
  const hit = conditions.find(([failed]) => failed);
  return hit ? hit[1] : undefined;
};

const GroupLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <li
    className="px-[11px] pt-2.5 pb-[5px] font-bold uppercase text-text-2 list-none"
    style={{ fontSize: 9.5, letterSpacing: '.1em' }}
  >
    {children}
  </li>
);

/** §05 — grouped overflow menu. Role gates hide; state gates disable with the reason. */
export const VideoCardMenu: FC<Props> = (props) => {
  const { video, refetch, brand, project, context, onRemoveFromVideoShortlist, share, title, onDeleted, onSettingsClosed } = props;
  const { role } = useUser();
  const [searchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddToStory, setShowAddToStory] = useState(false);
  const [showShortlist, setShowShortlist] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showPreviousVersions, setShowPreviousVersions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Fetched while the delete confirmation is open: is the video live on a PDP?
  const deleteImpact = useDeleteImpact(showDeleteConfirm && video?.id ? videoDeleteImpact(Number(video.id)) : null);
  const pdpWarning = pdpImpactWarning(deleteImpact, 'video');
  const [showUnapproveConfirm, setShowUnapproveConfirm] = useState(false);
  const [fetchedBrand, setFetchedBrand] = useState<any>(null);

  const st = getVideoStatus(video, project);
  const internalUser = isInternal(role);
  const videoBrand = brand || video?.brandOwners?.[0] || fetchedBrand;
  const projectId = project?.id || video.projectId || (video as any).project?.id;

  const hasPreviousVersions = !!projectId && st.hasFile && canViewPreviousVersions(role, video);
  const embedId = context?.embedId ?? (parseInt(searchParams.get('embed') || '', 10) || undefined);
  const shareComments = getReviewComments((video as any)?.comments);
  const publicShare = usePublicShare();

  const close = () => setAnchorEl(null);

  const resolveBrand = async (): Promise<any> => {
    if (videoBrand) return videoBrand;
    try {
      const { data } = await axiosInstant.get(getBrandByVideoId(String(video.id)));
      if (data) {
        setFetchedBrand(data);
        return data;
      }
    } catch {
      /* fall through */
    }
    toast.error('Unable to load brand information for this video.');
    return null;
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstant.delete(deleteVideo(video.id.toString()));
      toast.success('Video deleted. An admin can restore it.');
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted();
      else refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      // A 403 already got the global "no permission" toast (api/index.ts).
      if (status !== 403) {
        toast.error(
          softDeleteErrorMessage(err, 'Failed to delete video. Please try again.', {
            VideoNotFound: 'This video is already deleted or no longer exists.',
          })
        );
      }
      // 404: already gone, so the card is stale — drop it like a delete would.
      if (status === 404) {
        setShowDeleteConfirm(false);
        if (onDeleted) onDeleted();
        else refetch();
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleUnapprove = async () => {
    try {
      // List payloads carry no brand data (the API includes it only when a
      // brandId filter is active) — resolve it like settings/add-to-story do.
      const resolved = project?.brands?.[0] ?? (await resolveBrand());
      if (!resolved?.id) return; // resolveBrand already surfaced the error
      if (!projectId) {
        toast.error('Please open this video from its project page to unapprove it.');
        return;
      }
      await axiosInstant.patch(unapproveBrandProjectFile(resolved.id, projectId, video.id));
      toast.success('Video unapproved successfully!');
      refetch();
    } catch {
      toast.error('Failed to unapprove video. Please try again.');
    }
  };

  const handleReimport = async () => {
    try {
      await axiosInstant.get(reimportFile(video.id));
      toast.success('Re-import started — the file will refresh once processing completes.');
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;
      if (status === 404) toast.error('File no longer exists.');
      else if (status === 400 && code === 'FileMissingPath') toast.error("File hasn't been delivered yet — nothing to re-import.");
      else toast.error('Re-import failed.');
    }
  };

  const handleCopyPath = async () => {
    const path = buildCopyPath(video, project);
    try {
      await navigator.clipboard.writeText(path);
      toast.success('Path copied to clipboard');
    } catch {
      toast.error('Could not copy path');
    }
  };

  const handleDownloadComments = async () => {
    const toastId = toast.loading('Preparing comments...');
    try {
      const thread = await fetchFileComments(publicShare, projectId, video.id);
      if (getReviewComments(thread).length === 0) {
        toast.error('This video has no comments to download.', { id: toastId });
        return;
      }
      await downloadCommentsPDF({
        comments: thread,
        videoName: title,
        projectTitle: project?.title,
        productTitle: project?.product?.title,
      });
      toast.success('Comments downloaded.', { id: toastId });
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? 'Failed to download comments.', { id: toastId });
    }
  };

  const handleOpenSettings = async () => {
    const resolved = await resolveBrand();
    if (resolved) setShowSettings(true);
  };

  const handleAddToStory = async () => {
    const resolved = await resolveBrand();
    if (resolved) setShowAddToStory(true);
  };

  // ---- item model: role gates hide, state gates disable ----
  // NO download entry here, deliberately. `video.location` is the RAW key —
  // it bypasses the approval gate, the watermark-only rule, the brand's
  // enabled_file_types allowlist and the licensing checkbox, all of which
  // DownloadBtn enforces. DownloadBtn already sits beside this kebab on both
  // the card (VideoCard.tsx) and the detail header. Production suppressed this
  // item for every role (VideoCardMenuV2 `showDownloadOptions={false}`);
  // VIDEO-SECTION-SPEC.md §5 documents it as live, which is a spec error —
  // it read the downloadUrl prop and missed the suppression flag below it.
  const canCopyPath = role === 'POST_PRODUCTION_ADMIN' || role === 'ADMIN' || isAM(role);
  const canUnapproveRole = internalUser && canApproveUnapproveVideos(role);

  const reviewButton = share ? undefined : context?.reviewButton;
  const iconReview = reviewButton === 'icon';
  const compactReview = reviewButton === 'compact' || iconReview;
  const reviewLabel = iconReview ? null : (compactReview && 'Review') || 'Review video';
  const showReviewButton = !!reviewButton && st.hasFile && st.key !== 'approved';

  const groups: Array<{ label: string; items: Entry[] }> = share
    ? [
        {
          label: 'Get',
          // On a share page the thread comes through the share link, so the
          // item shows like it does in the app (an empty thread says so);
          // the page's own per-file comments carry no authors to count.
          items:
            publicShare || shareComments.length > 0
              ? [
                  {
                    key: 'share-comments',
                    label: 'Download comments',
                    icon: ICONS.comment,
                    onClick: handleDownloadComments,
                  },
                ]
              : [],
        },
      ]
    : [
        {
          label: 'Get',
          items: [
            ...(canCopyPath
              ? [{ key: 'copy', label: 'Copy path', icon: ICONS.copy, onClick: handleCopyPath }]
              : []),
            ...(projectId
              ? [{ key: 'comments', label: 'Download comments', icon: ICONS.comment, onClick: handleDownloadComments }]
              : []),
          ],
        },
        {
          label: 'Organize',
          items: [
            ...(internalUser && !context?.inShortlistDetail
              ? [
                  {
                    key: 'shortlist',
                    label: 'Add to shortlist',
                    icon: ICONS.star,
                    onClick: () => setShowShortlist(true),
                    disabled: !st.hasFile,
                    reason: !st.hasFile ? 'no file yet' : undefined,
                  },
                ]
              : []),
            ...(internalUser && onRemoveFromVideoShortlist
              ? [{ key: 'unshortlist', label: 'Remove from shortlist', icon: ICONS.remove, onClick: onRemoveFromVideoShortlist }]
              : []),
            ...(!internalUser
              ? [
                  {
                    key: 'story',
                    label: 'Add to story',
                    icon: ICONS.story,
                    onClick: handleAddToStory,
                    disabled: !st.hasFile,
                    reason: !st.hasFile ? 'no file yet' : undefined,
                  },
                ]
              : []),
            ...(st.hasFile && embedId
              ? [
                  {
                    key: 'unstory',
                    label: 'Remove from story',
                    icon: ICONS.remove,
                    onClick: async () => {
                      await axiosInstant.delete(removeVideoFromEmbed(embedId, video.id));
                      refetch();
                    },
                  },
                ]
              : []),
          ],
        },
        {
          label: 'Edit',
          items: [
            ...(internalUser || video.final
              ? [
                  {
                    key: 'settings',
                    label: 'Edit settings',
                    icon: ICONS.edit,
                    onClick: handleOpenSettings,
                    disabled: !st.hasFile,
                    reason: !st.hasFile ? 'no file yet' : undefined,
                  },
                ]
              : []),
            ...(hasPreviousVersions
              ? [
                  {
                    key: 'previousVersions',
                    label: 'View Previous Versions',
                    icon: ICONS.review,
                    onClick: () => setShowPreviousVersions(true),
                  },
                ]
              : []),
            ...(showReviewButton ? [] : [{
              key: 'reviewVideo',
              label: 'Review video',
              icon: ICONS.review,
              onClick: () => setShowReview(true),
              disabled: !st.hasFile || st.key === 'approved',
              reason: disabledReason([
                [!st.hasFile, 'no file yet'],
                [st.key === 'approved', 'approved'],
              ]),
            }]),
            ...(isAM(role)
              ? [
                  {
                    key: 'reimport',
                    label: 'Re-import',
                    icon: ICONS.reimport,
                    onClick: handleReimport,
                    disabled: !st.hasFile,
                    reason: !st.hasFile ? 'no file yet' : undefined,
                  },
                ]
              : []),
            ...(canUnapproveRole
              ? [
                  {
                    key: 'unapprove',
                    label: 'Unapprove',
                    icon: ICONS.unapprove,
                    onClick: () => setShowUnapproveConfirm(true),
                    disabled: st.key !== 'approved' || !projectId,
                    reason: disabledReason([
                      [st.key !== 'approved', 'not approved'],
                      [!projectId, 'needs project'],
                    ]),
                  },
                ]
              : []),
          ],
        },
        // Danger group (label-less = divider). DELETE /videos/:id is a soft
        // delete an admin can undo from the section's "Deleted only" view, so
        // it is gated like the server: admin | account manager admin (isAM).
        {
          label: '',
          items: isAM(role)
            ? [
                {
                  key: 'delete',
                  label: 'Delete video',
                  icon: ICONS.trash,
                  onClick: () => setShowDeleteConfirm(true),
                  danger: true,
                },
              ]
            : [],
        },
      ];

  const visibleGroups = groups.filter((g) => g.items.length > 0);
  if (visibleGroups.length === 0 && !showReviewButton) return null;

  const itemSx = (e: Entry) => {
    let color = 'var(--text)';
    if (e.danger) color = 'var(--down)';
    else if (e.disabled) color = 'var(--muted)';
    let hoverBg = 'var(--surface-2)';
    if (e.disabled) hoverBg = 'transparent';
    else if (e.danger) hoverBg = 'var(--down-bg)';
    return {
      height: 38,
      borderRadius: '8px',
      px: '11px',
      mx: '6px',
      gap: '11px',
      fontSize: 13,
      fontFamily: 'inherit',
      color,
      cursor: e.disabled ? 'not-allowed' : 'pointer',
      '&:hover': { background: hoverBg },
    };
  };

  return (
    <>
      {showReviewButton && (
        <Button
          variant="contained"
          color="primary"
          title="Review video"
          icon={<IconPlayBox size={compactReview ? 15 : 18} stroke="currentColor" />}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setShowReview(true);
          }}
          className={classNames('!flex items-center justify-center !min-w-0', {
            '!h-[30px] !w-[30px] !px-0': iconReview,
            '!h-[30px] !px-2.5 !text-[12px] gap-1.5': compactReview && !iconReview,
            '!h-10 !px-4 !text-[14px] gap-2 order-first': !compactReview,
          })}
        >
          {reviewLabel}
        </Button>
      )}
      {visibleGroups.length > 0 && (
      <button
        type="button"
        aria-label="More actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        className="inline-flex items-center justify-center w-7 h-[30px] rounded-lg text-text-2 hover:bg-surface-2 transition-colors shrink-0"
      >
        {ICONS.kebab}
      </button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              width: 266,
              borderRadius: '12px',
              border: '1px solid var(--border)',
              boxShadow: 'var(--sh-2)',
              background: 'var(--surface)',
              backgroundImage: 'none',
            },
          },
        }}
        MenuListProps={{ sx: { py: '6px' } }}
      >
        {visibleGroups.map((g) => [
          g.label ? (
            <GroupLabel key={`gl-${g.label}`}>{g.label}</GroupLabel>
          ) : (
            <Box key="divider" className="h-px mx-[11px] my-1.5 bg-border-token" component="li" />
          ),
          ...g.items.map((e) => (
            <MenuItem
              key={e.key}
              component={e.href ? 'a' : 'li'}
              {...(e.href ? { href: e.href, download: true } : {})}
              disableRipple
              onClick={() => {
                if (e.disabled) return;
                close();
                e.onClick?.();
              }}
              sx={itemSx(e)}
            >
              <span className={`inline-flex ${e.danger ? '' : 'text-text-2'}`} style={{ opacity: e.disabled ? 0.6 : 1 }}>
                {e.icon}
              </span>
              <span className="flex-1 min-w-0 truncate">{e.label}</span>
              {e.disabled && e.reason && (
                <span className="uppercase font-bold text-muted" style={{ fontSize: 10, letterSpacing: '.04em' }}>
                  {e.reason}
                </span>
              )}
            </MenuItem>
          )),
        ])}
      </Menu>

      {showSettings && videoBrand && (
        <SettingsVideoModal
          video={video}
          brand={videoBrand}
          onClose={() => {
            setShowSettings(false);
            onSettingsClosed?.();
          }}
          onComplete={() => refetch?.()}
        />
      )}
      {videoBrand && (
        <AddToStoryModalV2
          open={showAddToStory}
          onClose={() => setShowAddToStory(false)}
          onComplete={() => refetch()}
          brandId={videoBrand.id?.toString() || ''}
          videoId={video.id}
        />
      )}
      <AddToVideoShortlistMenu open={showShortlist} onClose={() => setShowShortlist(false)} s3FileId={video.id} />
      {showPreviousVersions && projectId && (
        <PreviousVersionsModal
          projectId={projectId}
          file={video}
          project={project}
          onClose={() => setShowPreviousVersions(false)}
        />
      )}
      {showReview && (
        <VariationReviewModal
          open={showReview}
          // Close refetches: "Submit Feedback" (request-edit) mutates final /
          // internal_status / project status WITHOUT firing onApproved, so a
          // plain close after it must still refresh. The approve path calls
          // onApproved() then onClose(), so the refetch here runs once.
          onClose={() => {
            setShowReview(false);
            refetch();
          }}
          project={project || video.project}
          variationFile={video}
          onApproved={() => setShowReview(false)}
        />
      )}
      <ConfirmationDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        disabled={deleting}
        title="Delete video"
        description={[
          `Delete “${title}” (${getVideoFilename(video) || 'no file'})? It leaves ${projectId ? 'its project and ' : ''}the video lists.${
            projectId ? ' Its slot shows the previous version again, or opens for a new upload.' : ''
          }`,
          ...(pdpWarning ? [pdpWarning] : []),
          `An admin can restore it from the Videos list: tick “${DELETED_TOGGLE_LABEL}”.${
            projectId ? ' Once a new video is uploaded in its place, it can no longer be restored.' : ''
          }`,
        ]}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
      <ConfirmationDialog
        open={showUnapproveConfirm}
        onClose={() => setShowUnapproveConfirm(false)}
        onConfirm={() => {
          setShowUnapproveConfirm(false);
          handleUnapprove();
        }}
        title="Unapprove video"
        description={`Are you sure you want to unapprove “${title}”?`}
        confirmLabel="Unapprove"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default VideoCardMenu;
